import { z } from 'zod';
import { addDays } from '../local-date.js';
import type { ParseResult, SchemaIssue } from '../voyage/schemas.js';
import {
  DAILY_DEPARTMENTS,
  DAILY_OBSERVATION_CATEGORIES,
  DAILY_REPORT_KINDS,
  DAILY_SAFETY_RESULTS,
  DAILY_SEA_STATES,
  DAILY_SUMMARY_MAX,
  DAILY_WEATHER,
  type DailyDepartment,
  type DailyReportKind,
} from './constants.js';
import { dailyIssues } from './task.js';

const optionalText = (max: number) =>
  z
    .string()
    .max(max, 'TOO_LONG')
    .nullish()
    .transform((v) => {
      const trimmed = v?.trim() ?? '';
      return trimmed === '' ? null : trimmed;
    });
const optionalEnum = <T extends readonly [string, ...string[]]>(values: T) =>
  z
    .enum(values)
    .nullish()
    .transform((v) => v ?? null);
const optionalDay = z
  .string()
  .regex(/^\d{4}-\d{2}-(0[1-9]|[12]\d|3[01])$/, 'INVALID_DATE')
  .nullish()
  .transform((v) => v ?? null);

/**
 * Türlerin alanları (ADR 0051 §5). Daily'nin bölümleri BGM "Daily Report Entry" sekmeleri
 * (General, Deck, Engine, Safety, Environment, Remarks); ötekiler ortağın taslağından.
 */
const DATA = {
  daily: z.strictObject({
    position: optionalText(200),
    weather: optionalEnum(DAILY_WEATHER),
    seaCondition: optionalEnum(DAILY_SEA_STATES),
    deck: optionalText(2000),
    engine: optionalText(2000),
    safety: optionalText(2000),
    environment: optionalText(2000),
    remarks: optionalText(2000),
    nextPort: optionalText(200),
  }),
  port: z.strictObject({
    port: optionalText(200),
    berth: optionalText(200),
    activities: optionalText(2000),
    remarks: optionalText(2000),
  }),
  defect_observation: z.strictObject({
    category: optionalEnum(DAILY_OBSERVATION_CATEGORIES),
    location: optionalText(200),
    assignedTo: optionalText(120),
    deadline: optionalDay,
    actionTaken: optionalText(2000),
    closed: z.boolean().default(false),
  }),
  safety: z.strictObject({
    topic: optionalText(200),
    result: optionalEnum(DAILY_SAFETY_RESULTS),
    nextDue: optionalDay,
    actionTaken: optionalText(2000),
    remarks: optionalText(2000),
  }),
} as const satisfies Record<DailyReportKind, z.ZodType>;

/** Gönderimde zorunlu alanlar (ADR 0051 §5). */
const REQUIRED: Readonly<Record<DailyReportKind, readonly string[]>> = {
  daily: ['position', 'weather', 'seaCondition'],
  port: ['port'],
  defect_observation: ['category', 'location'],
  safety: ['topic', 'result'],
};

const head = z.strictObject({
  kind: z.enum(DAILY_REPORT_KINDS),
  reportedAt: z.iso.datetime({ offset: false }),
  tzOffsetMin: z
    .number()
    .int()
    .min(-14 * 60)
    .max(14 * 60),
  department: optionalEnum(DAILY_DEPARTMENTS),
  summary: z
    .string()
    .max(DAILY_SUMMARY_MAX, 'TOO_LONG')
    .nullish()
    .transform((v) => v?.trim() ?? ''),
  data: z.record(z.string(), z.unknown()).default({}),
});

export interface DailyReportInput {
  kind: DailyReportKind;
  reportedAt: string;
  tzOffsetMin: number;
  /** Gemi yerel günü: `reportedAt` + fark (ADR 0051 §5); girilmez, türer. */
  reportDay: string;
  department: DailyDepartment | null;
  summary: string;
  data: Record<string, unknown>;
}

/** Anın gemi yerel günü. */
export function reportDayOf(reportedAt: string, tzOffsetMin: number): string {
  return new Date(Date.parse(reportedAt) + tzOffsetMin * 60_000).toISOString().slice(0, 10);
}

/** Gönderimde eksik alanlar (yol listesi). */
export function dailyReportMissing(report: DailyReportInput): SchemaIssue[] {
  const issues: SchemaIssue[] = [];
  if (report.summary === '') issues.push({ path: 'summary', code: 'REQUIRED' });
  for (const field of REQUIRED[report.kind]) {
    if (report.data[field] === null || report.data[field] === undefined) {
      issues.push({ path: `data.${field}`, code: 'REQUIRED' });
    }
  }
  if (report.kind === 'defect_observation') {
    // Kapanan gözlemde yapılan işlem, açık gözlemde sorumlu (ortağın taslağı "Assigned To")
    const field = report.data.closed === true ? 'actionTaken' : 'assignedTo';
    if (report.data[field] === null) issues.push({ path: `data.${field}`, code: 'REQUIRED' });
  }
  if (report.kind === 'safety' && report.data.result === 'failed' && !report.data.actionTaken) {
    issues.push({ path: 'data.actionTaken', code: 'ACTION_REQUIRED' });
  }
  return issues;
}

/**
 * Raporu doğrular ve biçimler (ADR 0051 §5). `draft`: biçim, liste ve tür alanları; `submit`:
 * ayrıca zorunlu alanlar. Gelecek tarih kayıt katmanında (gemi sunucusu saati).
 */
export function parseDailyReport(
  input: unknown,
  stage: 'draft' | 'submit',
): ParseResult<DailyReportInput> {
  const parsed = head.safeParse(input);
  if (!parsed.success) return { ok: false, issues: dailyIssues(parsed.error) };
  const value = parsed.data;
  const data = DATA[value.kind].safeParse(value.data);
  if (!data.success) return { ok: false, issues: dailyIssues(data.error, ['data']) };
  const report: DailyReportInput = {
    kind: value.kind,
    reportedAt: new Date(value.reportedAt).toISOString(),
    tzOffsetMin: value.tzOffsetMin,
    reportDay: reportDayOf(value.reportedAt, value.tzOffsetMin),
    department: value.department,
    summary: value.summary,
    data: data.data as Record<string, unknown>,
  };
  if (stage === 'submit') {
    const missing = dailyReportMissing(report);
    if (missing.length > 0) return { ok: false, issues: missing };
  }
  return { ok: true, value: report };
}

export interface DailyReportFile {
  sha256: string;
  name: string;
  size: number;
  mimeType: string;
}

/**
 * Revizyonun içerik nesnesi (ADR 0051 §6–§7): hash bunun kanonik JSON'undan (JCS) hesaplanır.
 * Revizyon no ve canlı ekler girer; ek sırası SHA-256'ya göredir (ekleme sırası içerik değildir).
 */
export function dailyReportContent(input: {
  reportId: string;
  revisionNo: number;
  report: DailyReportInput;
  files: readonly DailyReportFile[];
}) {
  const { report } = input;
  return {
    reportId: input.reportId,
    revisionNo: input.revisionNo,
    kind: report.kind,
    reportedAt: report.reportedAt,
    tzOffsetMin: report.tzOffsetMin,
    reportDay: report.reportDay,
    department: report.department,
    summary: report.summary,
    data: report.data,
    files: [...input.files]
      .sort((a, b) => a.sha256.localeCompare(b.sha256) || a.name.localeCompare(b.name))
      .map((f) => ({ sha256: f.sha256, name: f.name, size: f.size, mimeType: f.mimeType })),
  };
}
export type DailyReportContent = ReturnType<typeof dailyReportContent>;

/** Ekranda görünen rapor durumu (ADR 0051 §6). */
export type DailyReportStatus = 'draft' | 'submitted' | 'approved' | 'final' | 'returned';

export function dailyReportView(input: {
  /** Onay altyapısının durumu (`ApprovalState.status`). */
  approval: string;
  /** Son gönderimin kuralındaki adım sayısı. */
  ruleSteps: number;
  kind: DailyReportKind;
  data: Record<string, unknown>;
}): DailyReportStatus {
  switch (input.approval) {
    case 'draft':
      return 'draft';
    case 'returned':
      return 'returned';
    case 'approved':
      return input.ruleSteps === 0 ? 'final' : 'approved';
    default:
      return 'submitted';
  }
}

/** Defect / Observation açık mı (içerikten; ADR 0051 §6). */
export function isObservationOpen(kind: DailyReportKind, data: Record<string, unknown>): boolean {
  return kind === 'defect_observation' && data.closed !== true;
}

/** "Reports by Type (Last N Days)": gün gün tür sayıları, eskiden yeniye. */
export function reportsByDay(
  reports: readonly { reportDay: string; kind: string }[],
  lastDay: string,
  days: number,
): { day: string; counts: Record<string, number> }[] {
  const out = Array.from({ length: days }, (_, i) => ({
    day: addDays(lastDay, i - days + 1),
    counts: {} as Record<string, number>,
  }));
  const index = new Map(out.map((d, i) => [d.day, i]));
  for (const report of reports) {
    const i = index.get(report.reportDay);
    if (i === undefined) continue;
    const counts = (out[i] as (typeof out)[number]).counts;
    counts[report.kind] = (counts[report.kind] ?? 0) + 1;
  }
  return out;
}
